// Client mirror of
// supabase/functions/generate-progress-claim/retention-carry-forward.ts
// (the app does not import across into supabase/functions; parity is enforced by
// the deno test in that directory).
//
// The modal's claim preview and the edge function must carry forward the same
// retention from prior claims, otherwise the figures Josh sees before pressing
// Generate differ from the ones in the PDF.

import { isPriorClaimPeriod } from "./claimSummary";
import { getOperationalRetentionAmount } from "./reconciliationSummary";

export interface RetentionCarryForwardRow {
  retention_amount?: number | string | null;
  paid_by_builder?: number | string | null;
  claim_month?: number | string | null;
  claim_year?: number | string | null;
  reconciliation_summary?: unknown;
}

/**
 * SEQ-161: a reconciled prior claim carries forward what the builder actually
 * withheld, not what SEQ claimed. getOperationalRetentionAmount is the canonical
 * accessor the rest of the app already displays from.
 */
function retentionForRow(row: RetentionCarryForwardRow): number {
  return getOperationalRetentionAmount({
    retention_amount: Number(row.retention_amount || 0),
    reconciliation_summary: row.reconciliation_summary,
  });
}

function roundCurrency(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/** Retention held on every claim except the one being generated. */
export function sumAccumulatedRetention(
  rows: readonly RetentionCarryForwardRow[],
  claimYear: number,
  claimMonth: number,
): number {
  return roundCurrency(
    rows
      .filter(
        (row) =>
          !(
            Number(row.claim_month) === claimMonth &&
            Number(row.claim_year) === claimYear
          ),
      )
      .reduce((sum, row) => sum + retentionForRow(row), 0),
  );
}

/**
 * Retention held on claims from periods strictly before this one that the
 * builder actually paid.
 */
export function sumPriorRetentionOnPaidClaims(
  rows: readonly RetentionCarryForwardRow[],
  claimYear: number,
  claimMonth: number,
): number {
  return roundCurrency(
    rows
      .filter((row) =>
        isPriorClaimPeriod(
          Number(row.claim_year),
          Number(row.claim_month),
          claimYear,
          claimMonth,
        ),
      )
      .filter((row) => Number(row.paid_by_builder || 0) > 0)
      .reduce((sum, row) => sum + retentionForRow(row), 0),
  );
}
